import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { DriverChatSeed } from '@component/DriverChat/types';
import {
  closeDriverChatPanel,
  openPanelWithSeed,
  openPanelConversation,
  selectDriverChat,
} from '@store/slices/driverChat/driverChatSlice';

const CHANNEL_NAME = 'driver_chat_channel';

type PanelState = {
  isPanelOpen: boolean;
  conversationId: number | null;
  /** Ride-scoped conversation seed, if the popup was opened from "Message driver". */
  panelSeed: DriverChatSeed | null;
};
type SyncMessage = { type: 'panel'; state: PanelState } | { type: 'hello' };

/**
 * Keeps the chat window open with the same conversation across browser tabs,
 * both the conversations list and a ride-scoped conversation.
 * A newly opened tab asks the others for the current panel state.
 */
export const useDriverChatTabSync = (enabled: boolean) => {
  const dispatch = useAppDispatch();
  const { isPanelOpen, panelConversationId, panelSeed } =
    useAppSelector(selectDriverChat);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const currentRef = useRef<PanelState>({
    isPanelOpen,
    conversationId: panelConversationId,
    panelSeed,
  });
  currentRef.current = {
    isPanelOpen,
    conversationId: panelConversationId,
    panelSeed,
  };
  const isFirstSyncRef = useRef(true);

  const receivedRef = useRef<PanelState | null>(null);

  useEffect(() => {
    if (!enabled || typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<SyncMessage>) => {
      const message = event.data;
      if (message?.type === 'hello') {
        const { isPanelOpen: open, panelSeed: seed } = currentRef.current;
        // A seed still waiting for its conversation is not shareable yet.
        if ((open || seed) && !(seed && seed.conversationId == null)) {
          channel.postMessage({ type: 'panel', state: currentRef.current });
        }
        return;
      }
      if (message?.type !== 'panel') return;
      const {
        isPanelOpen: open,
        conversationId,
        panelSeed: seed,
      } = message.state;
      receivedRef.current = message.state;
      if (seed) {
        dispatch(openPanelWithSeed(seed));
      } else if (open) {
        dispatch(openPanelConversation(conversationId));
      } else {
        dispatch(closeDriverChatPanel());
      }
    };
    channel.postMessage({ type: 'hello' });

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [enabled]);

  const panelSeedConversationId = panelSeed?.conversationId ?? null;
  useEffect(() => {
    // A fresh tab starts closed; announcing that would close the other tabs' windows.
    if (isFirstSyncRef.current) {
      isFirstSyncRef.current = false;
      return;
    }
    // A seed without a conversation is still loading: nothing to share yet.
    if (panelSeed && panelSeed.conversationId == null) return;
    const received = receivedRef.current;
    if (
      received &&
      received.isPanelOpen === isPanelOpen &&
      received.conversationId === panelConversationId &&
      (received.panelSeed?.conversationId ?? null) === panelSeedConversationId
    ) {
      receivedRef.current = null;
      return;
    }
    channelRef.current?.postMessage({
      type: 'panel',
      state: {
        isPanelOpen,
        conversationId: panelConversationId,
        panelSeed,
      },
    });
  }, [isPanelOpen, panelConversationId, panelSeedConversationId]);
};
